.wrapper {
  --ds-menu-button-popover-origin: inherit;
  --ds-button-justify-content: stretch;
  display: contents;
}

.wrapper:not([data-label-visually-hidden]) {
  --ds-button-inner-justify-content: space-between;
}

.list {
  min-width: 150px;
  outline: none;
  border-radius: inherit;
  display: grid;
  grid-template-columns: var(--space__sm) auto auto 1fr auto var(--space__sm);
  grid-template-areas: '. check icon label end .';
  row-gap: var(--ds-listbox-row-gap, var(--space__xs));

  /*
   * The list scrolls, and its height cap lives here rather than on the popover. Both come from
   * react-aria:
   *
   * - It tells the popover that this list is its `scrollRef`, and its keyboard handler scrolls that
   *   element to keep the focused row in view. With the overflow on the popover instead, arrow-keying
   *   down a long list walked the focus out of sight — measured on a 29-hit search, row 35 sat 135px
   *   below the popover's bottom edge while the popover stayed at half its scroll range.
   * - It writes `max-height: <room left on screen>` onto the popover as an inline style, which beats
   *   any class rule, so a cap declared on `.popover` would never apply at all. `min-height: 0`,
   *   inside the flex column `.popover` now is, is what lets this list shrink below its own cap when
   *   the screen is the tighter of the two — so exactly one element scrolls, either way round.
   *
   * 24rem is about nine rows: enough to browse a result list, little enough to leave the page behind
   * it visible rather than covering the viewport with a column of white.
   */
  max-height: 24rem;
  min-height: 0;
  overflow: auto;
}

/*
 * The list's density is themeable by one surface at a time through the `--ds-listbox-*` properties:
 * the popover is portalled to `body`, so a surface sets them where the portal inherits them (the Arvo
 * console does, in `views/arvo/shell/arvo-shell.module.css`). Unset, they fall back to these defaults.
 */
.popover {
  border: 1px solid var(--color__gray-300);
  border-radius: var(--ds-listbox-radius, var(--border-radius__sm));
  background: var(--color__white);
  box-shadow: var(--ds-listbox-shadow, none);
  transition:
    transform 200ms,
    opacity 200ms;
  padding: var(--space__xs);

  /* So the list inside can be told to shrink and scroll instead of running past the screen — see `.list`. */
  display: flex;
  flex-direction: column;

  &[data-entering],
  &[data-exiting] {
    transform: var(--trigger);
    opacity: 0;
  }

  &[data-placement='top'] {
    --trigger: translateY(8px);
  }

  &[data-placement='bottom'] {
    --trigger: translateY(-8px);
  }

  &[data-placement='right'] {
    --trigger: translateX(-8px);
  }

  &[data-placement='left'] {
    --trigger: translateX(8px);
  }
}

/*
 * `--trigger-width` is measured by `ListBox` from the trigger box it was handed — see its `triggerRef`.
 * `--ds-popover-min-inline-size` is `ListBox`'s `popoverMinInlineSize`: a floor for a trigger that
 * can get narrower than its rows can usefully be, set through the popover's style because the
 * popover is portalled away from anything a caller's stylesheet could reach.
 */
.popover.match-trigger-width {
  min-inline-size: max(var(--trigger-width), var(--ds-popover-min-inline-size, 0px));
}

/*
 * A row is one line of text, so its block padding is a step below its inline padding rather than
 * equal to it: `sm` on all four sides made a 32px row out of 14px type, and nine of those fill the
 * list's 24rem cap. At `xs` the row is 24px and twelve fit, which is what a list you scan wants.
 *
 * The `column-gap` is two steps above the `xs` the spacing table gives an icon-to-text gap, because
 * here that token would render at its literal value: the streamline icons carry no padding inside
 * their viewBox (artwork runs 0.5 to 13.5 of 14) and a typography component's box is trimmed to cap
 * height, so both sides of the gap are ink and the reader sees the whole 4px as white. The table's
 * figure is a promise about the visual gap, not the declared one — `design/10-foundations.md`,
 * "Optical spacing". `md` rather than `sm` because the icon here reads as a column down the list
 * rather than a glyph tucked against one word, and a 24px row leaves it nothing else to separate
 * from the name with.
 */
.item {
  padding: var(--ds-listbox-item-padding-block, var(--space__xs)) var(--space__sm);
  border-radius: calc(var(--border-radius__lg) - var(--space__xs));
  outline: none;
  font-size: var(--font-size__md);
  color: var(--color__gray-800);
  position: relative;
  align-items: baseline;
  cursor: pointer;
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  column-gap: var(--space__md);
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;

  &[href] {
    text-decoration: none;
  }

  /* The `outline: none` above ties with `reset.css`'s ring floor, `:where(…[tabindex])
     :focus-visible` at (0,1,0), and loses to it when that sheet loads later. A row takes a
     roving `tabindex`, so a keyboard-focused or, on some browsers, clicked row painted the
     purple ring inside the list (PLT-7272, Roni). A row's focus is the gray tint below;
     this rule at (0,2,0) keeps it that. */
  &:focus-visible {
    outline: none;
  }
}

/*
 * Hover and focus are a gray tint, the same ground the nav and organisation-menu rows already hover
 * on. Blue is not available for this: `design/10-foundations.md` gives it exactly one job, the
 * selected segment of a pill tab switcher, and a menu of ten hits painted a row of `blue-100` was
 * the loudest thing on the page. PLT-6705 is settling one hover-and-focus language across every
 * surface; whatever tint it lands on belongs here too, and is a smaller move from gray than blue.
 */
.item:not([data-disabled], [data-unavailable]):is([data-open], [data-hovered], [data-focused]) {
  background: var(--color__gray-100);
}

.item[data-selected] {
  background: var(--color__gray-200);
}

/*
 * A selected row still owes the reader a hover state, and now that hover is a gray it cannot pay it
 * by going lighter: at `gray-100` the row would look like its selection was coming off. It darkens a
 * step instead, so it stays the heaviest row in the list whether the pointer is on it or not.
 */
.item[data-selected]:not([data-disabled], [data-unavailable]):is([data-open], [data-hovered], [data-focused]) {
  background: var(--color__gray-300);
}

.item[data-disabled] {
  cursor: not-allowed;
}

.item:is([data-disabled], [data-unavailable]):not([data-open], [data-hovered], [data-focused]) {
  background: var(--color__gray-100);
  color: var(--color__gray-400);
}

.item[data-unavailable]:is([data-open], [data-hovered], [data-focused]) {
  background: var(--color__gray-200);
  color: var(--color__gray-400);
}

.label {
  grid-area: label;
}

/*
 * What `renderEmptyState` renders, positioned by the list rather than by whoever passes it. `.list` is
 * a six-column grid whose first column is an `--space__sm` gutter, so a node handed to it unstyled
 * lands in that 8px column and is squeezed to a stack of single letters. Spanning every column and
 * carrying a row's own padding and text treatment means a caller passes words and nothing else —
 * `ListBox` applies this, so no view has to know the grid exists.
 */
.empty-state {
  grid-column: 1 / -1;
  padding: var(--space__sm);
  font-size: var(--font-size__md);
  color: var(--color__gray-600);
}
